/**
 * Class to render a timeline widget for Security log
 */
class SpbcTimelineWidget {
    // Static constants
    static markerRadius = 4;
    static hoursRangeMax = 24;
    static axisStepsCount = 6;
    static userActionsUpstep = 12;
    static legendCircleX = -120;
    static legendTextX = -105;

    /**
     * Constructor.
     */
    constructor() {
        // Current time properties
        this.now = new Date();
        this.currentTimestamp = Math.floor(this.now.getTime() / 1000);

        // Data properties
        this.eventsData = null;
        this.timelineMarkersData = [];
        this.timelineKnownEventsData = [];
        this.timelineEventTooltip = null;
        this.timelineStartTimestamp = this.currentTimestamp - SpbcTimelineWidget.hoursRangeMax * 3600;
        this.usersCount = 0;
        this.summaryEventsCount = 0;
    }

    /**
     * Initialize and render the timeline widget
     */
    render() {
        if (typeof spbcTimelineWidgetData === 'undefined') {
            return;
        }

        this.timelineEventTooltip = document.getElementById('spbc-timeline-tooltip');
        this.eventsData = spbcTimelineWidgetData.users_actions;
        this.timelineStartTimestamp = Number(spbcTimelineWidgetData.first_event_timestamp);
        this.timelineKnownEventsData = spbcTimelineWidgetData.known_events_data;
        this.timelineMarkersData = this.generateMarkersData(spbcTimelineWidgetData.found_types);

        const activeTab = document.querySelector('.spbc_tab_section--active');
        if (activeTab && activeTab.id === 'spbc_tab-security_log') {
            this.initTimeline();
        }

        // Initialize timeline when tab is loaded
        window.addEventListener('spbctTabLoaded', (event) => {
            if (event.detail.tab === 'security_log') {
                this.initTimeline();
            }
        });

        jQuery('#spbc-timeline-tab').on('click', (e)=> {
            if (e.hasOwnProperty('originalEvent')) {
                if (localStorage.getItem('spbc_timeline_closed') !== '1') {
                    localStorage.setItem('spbc_timeline_closed', '1');
                } else {
                    localStorage.setItem('spbc_timeline_closed', '0');
                }
            }
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            const userSelect = document.getElementById('spbc-timeline-user-select');
            if (userSelect.value) {
                this.drawUserTimeline(userSelect.value);
            }
        });
    }

    /**
     * Initialize timeline with user selection
     */
    initTimeline() {
        const userSelect = document.getElementById('spbc-timeline-user-select');
        const users = Object.keys(this.eventsData);
        for (let key in this.eventsData) {
            if (this.eventsData.hasOwnProperty(key)) {
                this.usersCount++;
                this.summaryEventsCount += this.eventsData[key].length;
            }
        }

        // Reset user selection dropdown
        userSelect.innerHTML = '';

        // Populate user options
        users.forEach((user) => {
            userSelect.appendChild(new Option(user, user));
        });

        // Add selection handler
        userSelect.addEventListener('change', () => {
            this.drawUserTimeline(userSelect.value);
        });

        // Draw initial timeline or show message
        if (users.length > 0) {
            this.drawUserTimeline(users[0]);
        } else {
            document.querySelector('.spbc-timeline-container').style.display = 'none';
            document.querySelector('.spbc-timeline-controls').style.display = 'none';
            document.getElementById('spbc-timeline-no-data').style.display = 'block';
        }

        const activitySelector = jQuery('#spbc_timeline_activity_counters')[0];
        activitySelector.innerText = activitySelector.innerText
            .replace('%users_count%', this.usersCount.toString())
            .replace('%events_count%', this.summaryEventsCount.toString());

        const isWidgetClosedByUser = (
            localStorage.getItem('spbc_timeline_closed') === '1'
        );
        if (!isWidgetClosedByUser) {
            setTimeout(function() {
                jQuery('#spbc-timeline-tab').trigger('click');
            }, 200);
        }
    }

    /**
     * Draw time axis on the timeline
     * @param {SVGElement} container - SVG container element
     * @param {number} width - Drawing area width
     * @param {number} height - Drawing area height
     */
    drawTimeAxis(container, width, height) {
        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const axis = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        height = height + 10;
        // Create main axis line
        axis.setAttribute('class', 'spbc-timeline-axis');
        axis.setAttribute('x1', '0');
        axis.setAttribute('y1', height);
        axis.setAttribute('x2', width);
        axis.setAttribute('y2', height);
        container.appendChild(axis);

        // Create axis ticks and labels
        for (let i = 0; i <= SpbcTimelineWidget.axisStepsCount; i++) {
            const timestamp = this.timelineStartTimestamp + (i / SpbcTimelineWidget.axisStepsCount) * timeRange;
            const x = (i / SpbcTimelineWidget.axisStepsCount) * width;

            // Create tick mark
            const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            tick.setAttribute('class', 'spbc-timeline-axis');
            tick.setAttribute('x1', x);
            tick.setAttribute('y1', height);
            tick.setAttribute('x2', x);
            tick.setAttribute('y2', height + 5);
            container.appendChild(tick);

            // Create time label
            const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            label.setAttribute('class', 'spbc-timeline-axis-text');
            label.setAttribute('x', x);
            label.setAttribute('y', height + 20);

            const date = new Date(timestamp * 1000);
            label.textContent = `${date
                .getHours()
                .toString()
                .padStart(2, '0')}:${date.getMinutes()
                .toString()
                .padStart(2, '0')}`;
            container.appendChild(label);
        }
    }

    /**
     * Create SVG element for an event
     * @param {Object} event - Event data
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {string} user - User name
     * @return {SVGElement} Event group element
     */
    createEventElement(event, x, y, user) {
        const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        group.setAttribute('transform', `translate(${x}, ${y})`);
        group.setAttribute('data-event', JSON.stringify({...event, user}));

        // Create event marker
        const marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        marker.setAttribute('class', 'spbc-timeline-event-marker');
        marker.setAttribute('r', SpbcTimelineWidget.markerRadius);
        marker.style.fill = this.timelineMarkersData[event.event].color;
        group.appendChild(marker);

        // Add event handlers
        group.addEventListener('mouseenter', this.showEventTooltip.bind(this));
        group.addEventListener('mouseleave', this.hideEventTooltip.bind(this));

        return group;
    }

    /**
     * Show event tooltip
     * @param {Event} event - Mouse event
     */
    showEventTooltip(event) {
        const eventData = JSON.parse(event.currentTarget.getAttribute('data-event'));
        const {user, event: eventName, time} = eventData;

        // Format date for display
        const formatDate = (timestamp) => {
            const date = new Date(timestamp * 1000);

            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            const month = months[date.getMonth()];
            const day = date.getDate();
            const dayWithZero = String(day).padStart(2, '0');
            const year = date.getFullYear();

            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');

            return `${month} ${dayWithZero} ${year} ${hours}:${minutes}:${seconds}`;
        };

        // Update tooltip content
        document.getElementById('spbc-timeline-tooltip--user').innerText = user;
        document.getElementById('spbc-timeline-tooltip--event').innerText = eventName;
        document.getElementById('spbc-timeline-tooltip--time').innerText = formatDate(time);

        // define tooltip position
        const tooltip= document.getElementById('spbc-timeline-tooltip');
        const fixedTop = event.offsetY + 35;
        const tooltipY = fixedTop.toString() + 'px';
        const screenWidth = window.innerWidth;
        const tooltipWidth = tooltip.offsetWidth ? tooltip.offsetWidth : 165;
        const tooltipEventPositionX = event.clientX;
        let tooltipX = tooltipEventPositionX - tooltipWidth;
        if (tooltipEventPositionX + (tooltipWidth * 2) > screenWidth) {
            tooltipX = tooltipEventPositionX - (tooltipWidth * 2);
        }

        tooltipX = tooltipX.toString() + 'px';
        tooltip.style.position = 'absolute';
        tooltip.style.top = tooltipY;
        tooltip.style.left = tooltipX;

        // Show tooltip
        this.timelineEventTooltip.classList.remove('spbc-timeline-hidden-element');
        jQuery('[data-event-type]').filter(function() {
            return jQuery(this).data('event-type') === eventName;
        }).addClass('spbc-timelime-highlight-on-tooltip');
    }

    /**
     * Hide event tooltip
     */
    hideEventTooltip() {
        this.timelineEventTooltip.classList.add('spbc-timeline-hidden-element');
        jQuery('[data-event-type]').removeClass('spbc-timelime-highlight-on-tooltip');
    }

    /**
     * Draw timeline for specific user
     * @param {string} user - Selected user name
     */
    drawUserTimeline(user) {
        const svg = document.getElementById('spbc-timeline-svg');
        const noDataMessage = document.getElementById('spbc-timeline-no-data');

        // Clear previous content
        svg.innerHTML = '';
        noDataMessage.style.display = 'none';

        let additionalEventsHeight = 0;
        if (spbcTimelineWidgetData.found_types.length > 5) {
            additionalEventsHeight = (spbcTimelineWidgetData.found_types.length - 5) * 12;
        }

        // Calculate dimensions
        const width = svg.parentElement.clientWidth;
        const height = 150 + additionalEventsHeight;

        // Setup drawing area
        const margin = {top: 10 + additionalEventsHeight, right: 40, bottom: 40, left: 140};
        const contentWidth = width - margin.left - margin.right;
        const contentHeight = height - margin.top - margin.bottom;
        const baseLineY = contentHeight / 2;

        svg.setAttribute('width', width);
        svg.setAttribute('height', height);

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `translate(${margin.left}, ${margin.top+30})`);
        svg.appendChild(g);

        // Calculate base positions


        // Draw axis
        this.drawTimeAxis(g, contentWidth, baseLineY + 10);

        // Filter user events
        const userEvents = (this.eventsData[user] || []).filter((event) =>
            event.time >= this.timelineStartTimestamp &&
            event.time <= this.currentTimestamp,
        );

        // Handle no events case
        if (userEvents.length === 0) {
            noDataMessage.style.display = 'block';
            return;
        }

        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const uniqueEventsCount = spbcTimelineWidgetData.found_types.length;

        // Draw event lines and legends
        for (let i = 0; i < uniqueEventsCount; i++) {
            const userLineY = baseLineY - (SpbcTimelineWidget.userActionsUpstep * i);
            const eventType = spbcTimelineWidgetData.found_types[i];
            const color = this.timelineMarkersData[eventType].color;

            // Draw event line
            const userLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            userLine.setAttribute('class', 'spbc-timeline-user-line');
            userLine.setAttribute('x1', '0');
            userLine.setAttribute('y1', userLineY);
            userLine.setAttribute('x2', contentWidth);
            userLine.setAttribute('y2', userLineY);
            userLine.setAttribute('data-event-type', eventType);
            g.appendChild(userLine);

            // Draw legend circle
            const legendCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            legendCircle.setAttribute('class', 'spbc-timeline-legend-circle');
            legendCircle.setAttribute('cx', SpbcTimelineWidget.legendCircleX);
            legendCircle.setAttribute('cy', userLineY);
            legendCircle.setAttribute('r', SpbcTimelineWidget.markerRadius);
            legendCircle.style.fill = color;
            g.appendChild(legendCircle);

            // Draw legend text
            const legendText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            legendText.setAttribute('class', 'spbc-timeline-legend-text');
            legendText.setAttribute('x', SpbcTimelineWidget.legendTextX);
            legendText.setAttribute('y', userLineY);
            legendText.setAttribute('text-anchor', 'start');
            legendText.setAttribute('dominant-baseline', 'middle');
            legendText.setAttribute('data-event-type', eventType);
            legendText.textContent = eventType;
            g.appendChild(legendText);
        }

        // Draw events
        userEvents.forEach((userEvent) => {
            const x = ((userEvent.time - this.timelineStartTimestamp) / timeRange) * contentWidth;
            const eventY = baseLineY + this.timelineMarkersData[userEvent.event].upstep;
            g.appendChild(this.createEventElement(userEvent, x, eventY, user));
        });
    }

    /**
     * Generate marker data for events
     * @param {Array} events - List of event types
     * @return {Object} Marker configuration data
     */
    generateMarkersData(events) {
        const colorMap = {};
        let upstep = 0;
        let colorScheme = 'default';

        events.forEach((userEvent) => {
            if (this.timelineKnownEventsData[userEvent] && this.timelineKnownEventsData[userEvent].color_scheme) {
                colorScheme = this.timelineKnownEventsData[userEvent].color_scheme;
            }
            colorMap[userEvent] = {
                color: colorScheme,
                upstep: upstep,
            };
            upstep -= SpbcTimelineWidget.userActionsUpstep;
        });

        return colorMap;
    }
}

/**
 * Wrapper
 */
function spbcTimelineWidgetRender() { // eslint-disable-line no-unused-vars
    const timelineWidget = new SpbcTimelineWidget();
    jQuery( function() {
        jQuery('#spbc-timeline-accordion').accordion({
            heightStyle: 'content',
            collapsible: true,
            active: false,
        });
    } );
    timelineWidget.render();
}

